function sortByPosition(tasks) {
    const list = [...tasks];
    if (list.some((t) => t.position !== undefined && t.position !== null)) {
        list.sort((a, b) => (a.position ?? Infinity) - (b.position ?? Infinity));
    }
    return list;
}

export function createTaskApi({ role, apiUrl, getToken }) {
    const isAdmin = role === "admin";

    const headers = (json = true) => {
        const h = { Authorization: `Bearer ${getToken()}` };
        if (json) h["Content-Type"] = "application/json";
        return h;
    };

    return {
        async fetchTasks() {
            const url = isAdmin ? `${apiUrl}/task/all` : `${apiUrl}/task/stafftask`;
            const res = await fetch(url, { headers: headers(false) });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || data.message || "Failed to fetch tasks");
            }
            const tasks = isAdmin ? data : data.data;
            return sortByPosition(Array.isArray(tasks) ? tasks : []);
        },

        async deleteTask(taskId) {
            const path = isAdmin ? `${apiUrl}/task/${taskId}` : `${apiUrl}/task/stafftask/${taskId}`;
            const res = await fetch(path, { method: "DELETE", headers: headers(false) });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.message || "Failed to delete task");
            }
        },

        async reorderTasks(orderedTasks) {
            const path = isAdmin ? `${apiUrl}/task/reorder` : `${apiUrl}/task/reorder-staff`;
            const res = await fetch(path, {
                method: "POST",
                headers: headers(),
                body: JSON.stringify({ orderedTasks }),
            });
            if (!res.ok) throw new Error("Failed to reorder tasks");
        },

        async logHistory(taskId, title, notes = null) {
            const path = isAdmin ? `${apiUrl}/task-history` : `${apiUrl}/task-history/staffhistory`;
            const res = await fetch(path, {
                method: "POST",
                headers: headers(),
                body: JSON.stringify({ title, notes, task_id: taskId }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.message || "Failed to log history");
            }
        },

        async updateStatus(taskId, status) {
            const path = isAdmin
                ? `${apiUrl}/task/change/${taskId}`
                : `${apiUrl}/task/changetask/${taskId}`;
            const res = await fetch(path, {
                method: "PUT",
                headers: headers(),
                body: JSON.stringify({ status }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.message || "Failed to update status");
            }
        },

        async updatePaused(taskId, paused) {
            const path = isAdmin
                ? `${apiUrl}/task/change/${taskId}`
                : `${apiUrl}/task/changetask/${taskId}`;
            const res = await fetch(path, {
                method: "PUT",
                headers: headers(),
                body: JSON.stringify({ paused }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.message || "Failed to update pause state");
            }
        },

        async updateField(taskId, field, value) {
            if (field === "attachment" && value instanceof File) {
                const formData = new FormData();
                formData.append("attachment", value);
                const path = isAdmin
                    ? `${apiUrl}/task/update/${taskId}`
                    : `${apiUrl}/task/updatetask/${taskId}`;
                const res = await fetch(path, {
                    method: "PATCH",
                    body: formData,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.message || "Update failed");
                return data?.updates ?? (data?.attachment ? { attachment: data.attachment } : {});
            }

            const path = isAdmin
                ? `${apiUrl}/task/${taskId}`
                : `${apiUrl}/task/stafftask/${taskId}`;
            const res = await fetch(path, {
                method: "PUT",
                headers: headers(),
                body: JSON.stringify({ [field]: value }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Update failed");
            return data?.updates ?? { [field]: value };
        },

        async fetchEmployees() {
            if (!isAdmin) return [];
            const res = await fetch(`${apiUrl}/staff`, { headers: headers(false) });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to fetch staff");
            return (Array.isArray(data) ? data : []).map((person) => ({
                label: `${person.lastName} ${person.firstName} (${person.emplyee_ID})`,
                value: person.id,
            }));
        },

        async bulkAssign(taskIds, assignTo) {
            if (!isAdmin) throw new Error("Not allowed");
            const results = await Promise.allSettled(
                taskIds.map((id) =>
                    fetch(`${apiUrl}/task/${id}`, {
                        method: "PUT",
                        headers: headers(),
                        body: JSON.stringify({ assign_to: assignTo }),
                    }).then(async (res) => {
                        if (!res.ok) {
                            const data = await res.json().catch(() => ({}));
                            throw new Error(data.message || "Assign failed");
                        }
                    })
                )
            );
            const failed = results.filter((r) => r.status === "rejected").length;
            if (failed > 0) {
                throw new Error(`${failed} task(s) could not be reassigned`);
            }
        },

        async completeOccurrence(taskId) {
            const res = await fetch(`${apiUrl}/task/${taskId}/complete-occurrence`, {
                method: "POST",
                headers: headers(),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.message || "Failed to complete recurring task");
            }
            return data;
        },

        async snoozeTask(taskId, minutes = 10) {
            const res = await fetch(`${apiUrl}/task/${taskId}/snooze`, {
                method: "POST",
                headers: headers(),
                body: JSON.stringify({ minutes }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.message || "Failed to snooze reminder");
            }
            return data;
        },
    };
}
